import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  asRows,
  catalogReview,
  contentLabel,
  contentTypeCounts,
  isServiceProposal,
  isSimulatedProposal,
  operationLabel,
  reviewDisposition,
  serviceIdentity,
} from '@/lib/menuChangeView';
import { menuDecisionKind } from '@/lib/menuChangeReview';
export function menuChangeState(ticket) {
  return ticket?.workflow_state?.menu_change || null;
}

function Field({ label, children, testId }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      <span className="break-words">{children}</span>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section className="space-y-1 border-t border-cp-line pt-2" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {children}
    </section>
  );
}

function CatalogList({ rows, added }) {
  if (!rows.length) return <span className="text-muted-foreground">None</span>;
  return (
    <ul className="list-disc pl-5">
      {rows.map((id) => (
        <li key={id}>
          <span className="font-mono">{id}</span>
          {id === added ? ' (added)' : ''}
        </li>
      ))}
    </ul>
  );
}

function ServiceList({ rows, added }) {
  if (!rows.length) return <span className="text-muted-foreground">None</span>;
  return (
    <ul className="list-disc pl-5">
      {rows.map((row) => {
        const label = contentLabel(row);
        const isAdded = added && row.obj_name === added.name && row.service_version === added.version;
        return (
          <li key={label} className="font-mono">
            {label}
            {isAdded ? ' (added)' : ''}
          </li>
        );
      })}
    </ul>
  );
}

function BeforeAfter({ side, value, added, addedService, testId }) {
  if (!value) return null;
  let body;
  if (Array.isArray(value.assigned_catalogs)) {
    body = <CatalogList rows={value.assigned_catalogs} added={side === 'After' ? added : ''} />;
  } else if (Array.isArray(value.services)) {
    body = <ServiceList rows={value.services} added={side === 'After' ? addedService : null} />;
  } else {
    body = (
      <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return (
    <div data-testid={testId}>
      <p className="font-medium">{side}</p>
      {body}
    </div>
  );
}

function roleCountText(catalog) {
  if (!catalog.impactComplete) return 'Not confirmed';
  if (catalog.roleCount !== null) return String(catalog.roleCount);
  return String(catalog.affectedRoles.length);
}

function CatalogContentApproval({
  proposal,
  catalog,
  execution,
  change,
  pending,
  canMutate,
  submitting,
  onApprove,
  onReject,
  simulated,
  disposition,
  automaticApproval = false,
}) {
  const applied = change.lifecycle === 'APPLIED' && execution.catalog_verified === true;
  const inProgress = change.lifecycle === 'APPROVED' || change.lifecycle === 'EXECUTING';
  const blocked = Boolean(catalog.blocker) && disposition !== 'read_only';
  const shared = catalog.impactComplete && Number(catalog.roleCount) > 1;
  const navigationUnverified = catalog.navigationVerified !== true;
  const transportId = execution.transport || execution.transport_id || '';
  const transportSummary = transportId
    || (catalog.transportPlan?.requests?.length ? 'Customizing, not created yet' : 'Customizing');
  const sapSummary = execution.sap_changed === true
    ? 'Changed'
    : inProgress
      ? 'Verification pending'
      : execution.sap_changed === false && execution.status
      ? 'Unchanged'
      : blocked
        ? 'Blocked'
        : navigationUnverified
          ? 'Navigation unverified'
          : 'Verified';
  const showResult = applied;
  const approve = () => onApprove?.({
    payloadHash: proposal.payload_hash,
    menuStateHash: proposal.menu_state_hash,
    reviewHash: catalog.reviewHash,
  });
  const reject = () => onReject?.({
    payloadHash: proposal.payload_hash,
    menuStateHash: proposal.menu_state_hash,
    reviewHash: catalog.reviewHash,
  });

  return (
    <Card data-testid="menu-change-review">
      <CardHeader>
        <CardTitle>{showResult ? 'Catalog change result' : 'Catalog change'}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        {simulated && (
          <p data-testid="menu-change-simulated" className="rounded-md border border-cp-mod px-3 py-2 font-semibold text-cp-mod">
            SIMULATED — this review is not a live SAP result.
          </p>
        )}
        {blocked && (
          <p data-testid="menu-change-blocker" className="rounded-md border border-cp-mod px-3 py-2 font-semibold text-cp-mod">
            SAP write is blocked ({catalog.blocker}).
          </p>
        )}
        {!catalog.impactComplete && (
          <p data-testid="menu-change-impact-unknown" className="rounded-md border border-cp-mod px-3 py-2 text-cp-mod">
            Shared-catalog impact is incomplete
            {catalog.rolesScanned != null ? `. HALO checked ${catalog.rolesScanned} role(s)` : ''}
            {catalog.scanTruncated ? ' and the scan stopped at the configured role limit' : ''}.
          </p>
        )}
        {shared && (
          <p className="rounded-md border border-cp-mod px-3 py-2 text-cp-mod">
            {catalog.roleCount} roles reference this catalog.
          </p>
        )}
        {navigationUnverified && !showResult && (
          <p data-testid="menu-change-navigation-limitation">
            Navigation parameters were not returned and remain unverified.
          </p>
        )}
        {change.lifecycle === 'RECONCILIATION_REQUIRED' && (
          <p data-testid="menu-change-reconciliation">Reconciliation required. Do not retry the SAP write.</p>
        )}
        {change.lifecycle === 'PARTIAL_SUCCESS' && (
          <p data-testid="menu-change-partial">SAP committed the catalog change. Transport recording still needs reconciliation.</p>
        )}
        {change.lifecycle === 'FAILED' && (
          <p data-testid="menu-change-technical-failure">The catalog change did not complete.</p>
        )}
        {inProgress && !pending && (
          <p data-testid="menu-change-verification-pending">
            Approval recorded. HALO is reconciling SAP before reporting the catalog change as applied.
          </p>
        )}

        {showResult ? (
          <div data-testid="menu-change-result" className="grid gap-1">
            <p>
              {catalog.fioriId || catalog.application || 'The application'} was added to{' '}
              <span className="font-mono">{catalog.destinationCatalog || catalog.catalog}</span>.
            </p>
            <Field label="SAP verification" testId="menu-change-sap">{sapSummary}</Field>
            <Field label="Transport" testId="menu-change-transport">{transportSummary}</Field>
            <p>Authorization review continues below.</p>
          </div>
        ) : (
          <div className="grid gap-1" data-testid="menu-change-content-proposal">
            <Field label="Application" testId="menu-change-application">
              {catalog.application || 'Not returned'}
            </Field>
            <Field label="Fiori ID" testId="menu-change-fiori">{catalog.fioriId || 'Not returned'}</Field>
            <Field label="Target role"><span className="font-mono">{proposal.role}</span></Field>
            <Field label="Source catalog" testId="menu-change-source-catalog">
              <span className="font-mono">{catalog.sourceCatalog || 'Not returned'}</span>
            </Field>
            <Field label="Destination catalog" testId="menu-change-catalog">
              <span className="font-mono">{catalog.destinationCatalog || catalog.catalog || 'Not returned'}</span>
            </Field>
            <Field label="Operation">Add 1 tile and 1 target mapping</Field>
            <Field label="Affected roles" testId="menu-change-role-count">{roleCountText(catalog)}</Field>
            <Field label="SAP verification">{sapSummary}</Field>
            <Field label="Transport" testId="menu-change-transport">{transportSummary}</Field>
            <p data-testid="menu-change-scope">
              Approval covers this catalog change only. PFCG authorization changes require a separate review.
            </p>
          </div>
        )}

        {execution.status && <p data-testid="menu-change-execution">{execution.status}</p>}
        {blocked && disposition === 'proposal_only' && (
          <p data-testid="menu-change-not-executed">Not executed. This proposal did not change SAP.</p>
        )}
        {proposal.executable === false && !['APPROVED', 'APPLIED'].includes(change.lifecycle) && disposition !== 'read_only' && !blocked && (
          <p data-testid="menu-change-not-executed">Not executed. This proposal did not change SAP.</p>
        )}

        <details data-testid="menu-change-technical">
          <summary className="cursor-pointer">Technical details</summary>
          <div className="mt-2 grid gap-1 text-xs text-muted-foreground">
            {catalog.tileId ? <p className="font-mono">Tile: {catalog.tileId}</p> : null}
            {catalog.targetMappingId ? <p className="font-mono">Target mapping: {catalog.targetMappingId}</p> : null}
            <p>Navigation parameters: {catalog.navigationVerified ? 'verified' : 'not returned'}</p>
            {proposal.payload_hash ? <p className="font-mono break-all">Payload {proposal.payload_hash}</p> : null}
            {proposal.menu_state_hash ? <p className="font-mono break-all">Menu baseline {proposal.menu_state_hash}</p> : null}
            {catalog.reviewHash ? <p className="font-mono break-all">Review {catalog.reviewHash}</p> : null}
            {catalog.transportPlan?.requests?.map((item) => (
              <p key={item.sequence} data-testid="menu-change-transport-plan">
                {item.transport_type} · {item.purpose} · {item.status}
                {transportId ? ` · ${transportId}` : ''}
              </p>
            ))}
            {proposal.detail ? <p data-testid="menu-change-validation">{proposal.detail}</p> : null}
            {execution.detail ? <p>{execution.detail}</p> : null}
            {catalog.affectedRoles.length > 0 && (
              <p data-testid="menu-change-affected-roles">{catalog.affectedRoles.join(', ')}</p>
            )}
            {asRows(proposal.alternatives).map((item) => (
              <p key={item.catalog_id} data-testid="menu-change-alternatives">
                Not selected: {item.catalog_id}. {item.note}
                {asRows(item.additional_applications).map((row) => row.obj_name || row.application || row.title).filter(Boolean).join(', ')}
              </p>
            ))}
          </div>
        </details>

        {pending && canMutate && !automaticApproval && (
          <div className="flex gap-2">
            <Button type="button" data-testid="menu-change-approve" disabled={submitting} onClick={approve}>
              Approve & Apply Catalog Change
            </Button>
            <Button type="button" variant="outline" data-testid="menu-change-reject" disabled={submitting} onClick={reject}>
              Reject menu change
            </Button>
          </div>
        )}
        {inProgress && canMutate && !applied && execution.catalog_verified !== true && execution.sap_changed !== true && execution.status && (
          <p data-testid="menu-change-catalog-retry">
            Catalog apply did not verify. {execution.detail || execution.status}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function MenuChangeApproval({
  ticket,
  onApprove,
  onReject,
  onExecute,
  submitting = false,
  canMutate = false,
  automaticApproval = false,
}) {
  const change = menuChangeState(ticket);
  const classification = ticket?.workflow_state?.classification;
  if (!change?.proposal) return null;
  const proposal = change.proposal || {};
  const operation = proposal.operation || '';
  const catalogAdd = operation === 'CATALOG_ADD';
  const catalogVerification = operation === 'VERIFY_CATALOG_ASSIGNMENT';
  const catalogContentModification = [
    'APPLICATION_TO_CATALOG',
    'CATALOG_REFRESH',
    'DEDICATED_CATALOG_CREATE',
  ].includes(operation);
  const service = isServiceProposal(proposal);
  const identity = serviceIdentity(proposal);
  const additions = asRows(proposal.additions);
  const removals = asRows(proposal.potential_removals);
  const unresolved = asRows(proposal.unresolved);
  const catalog = catalogReview(proposal);
  const contents = catalog.additionalApplications;
  const contentTypes = contentTypeCounts(contents);
  const execution = change.execution || {};
  const closed = ['EXECUTING', 'APPLIED', 'PARTIAL_SUCCESS', 'FAILED', 'RECONCILIATION_REQUIRED', 'REJECTED'].includes(change.lifecycle);
  const pending = change.lifecycle === 'PENDING_APPROVAL' && !closed;
  const approvedCatalogContent = change.lifecycle === 'APPROVED'
    && operation === 'APPLICATION_TO_CATALOG';
  const alreadyAssigned = change.lifecycle === 'ALREADY_ASSIGNED' || execution.status === 'ALREADY_ASSIGNED';
  const applicationToCatalog = operation === 'APPLICATION_TO_CATALOG';
  const showRefreshDelta = !catalogAdd && !catalogVerification && !applicationToCatalog && !service;
  const decision = menuDecisionKind(ticket);
  const disposition = reviewDisposition(proposal);
  const simulated = isSimulatedProposal(proposal, execution);
  if (applicationToCatalog) {
    return (
      <CatalogContentApproval
        proposal={proposal}
        catalog={catalog}
        execution={execution}
        change={change}
        pending={pending}
        canMutate={canMutate}
        submitting={submitting}
        onApprove={onApprove}
        onReject={onReject}
        simulated={simulated}
        disposition={disposition}
        automaticApproval={automaticApproval}
      />
    );
  }

  const statusLabel = catalogVerification
    ? 'VERIFIED / NO_CHANGE_REQUIRED'
    : decision === 'rejected'
      ? 'Rejected'
      : decision === 'failed'
        ? `Technical failure (${change.lifecycle})`
        : change.lifecycle;

  return (
    <Card data-testid="menu-change-review">
      <CardHeader>
        <CardTitle>{catalogVerification ? 'Catalog Assignment Verification' : 'Menu change approval'}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        {simulated && (
          <p data-testid="menu-change-simulated" className="rounded-md border border-cp-mod px-3 py-2 font-semibold text-cp-mod">
            SIMULATED — this review is not a live SAP result.
          </p>
        )}
        <div className="grid gap-1">
          <Field label="SAP system">
            {proposal.target_system} client {proposal.target_client}
          </Field>
          <Field label="Target role">
            <span className="font-mono">{proposal.role}</span>
          </Field>
          <Field label="Operation">
            {operationLabel(operation)}
            {operation && operationLabel(operation) !== operation ? (
              <span className="text-muted-foreground"> ({operation})</span>
            ) : null}
          </Field>
          {!service && catalog.sourceCatalog && (
            <Field label="Source catalog" testId="menu-change-source-catalog">
              <span className="font-mono">{catalog.sourceCatalog}</span>
            </Field>
          )}
          {!service && catalog.catalog && (
            <Field label={catalog.sourceCatalog ? 'Destination catalog' : 'Catalog ID'} testId="menu-change-catalog">
              <span className="font-mono">{catalog.destinationCatalog || catalog.catalog}</span>
            </Field>
          )}
          {catalog.fioriId && (
            <Field label="Fiori ID" testId="menu-change-fiori">{catalog.fioriId}</Field>
          )}
          {!catalogAdd && catalog.application && (
            <Field label="Application" testId="menu-change-application">{catalog.application}</Field>
          )}
          {catalog.tcode && <Field label="TCode clue">{catalog.tcode}</Field>}
          {service && (
            <>
              <Field label="OData service" testId="menu-change-service">
                <span className="font-mono">{[identity.objectType, identity.name].filter(Boolean).join(' ')}</span>
              </Field>
              <Field label="Service version">{identity.version || 'n/a'}</Field>
            </>
          )}
          <Field label="Status" testId="menu-change-status">
            {statusLabel}
          </Field>
          {pending && (
            <p data-testid="menu-change-revalidation-note" className="text-muted-foreground">
              HALO rechecks the SAP role, catalog assignment, and catalog baseline before approval and again before execution. This proposal does not expire because time passes.
            </p>
          )}
          {alreadyAssigned && (
            <p data-testid="menu-change-already-assigned">
              Already assigned. No change is needed. SAP was not changed.
            </p>
          )}
          {!catalogVerification && (
            <Field label="Menu baseline">
              <span className="font-mono text-xs">{proposal.menu_state_hash}</span>
            </Field>
          )}
          {classification?.operation && (
            <div data-testid="menu-change-classification" className="text-muted-foreground">
              Classified as {classification.operation}. {classification.reason || ''}
              {classification.source ? ` Source: ${classification.source}.` : ''}
            </div>
          )}
        </div>

        {(proposal.detail || proposal.lookup_status) && (
          <Section title="SAP validation" testId="menu-change-validation">
            {proposal.detail && <p>{proposal.detail}</p>}
            {proposal.lookup_status && (
              <p>
                Service lookup {proposal.lookup_status}
                {proposal.activation_status ? `, activation ${proposal.activation_status}` : ''}
                {proposal.preview_status ? `, preview ${proposal.preview_status}` : ''}.
              </p>
            )}
          </Section>
        )}

        <Section title={catalogVerification ? 'Verification result' : 'Proposed change'}>
          {catalogVerification ? (
            <div data-testid="menu-change-verification-result">
              <p className="text-lg font-semibold">
                {proposal.verification_result?.answer || 'UNKNOWN'} — {proposal.verification_result?.code || 'VERIFICATION_UNAVAILABLE'}
              </p>
              <p>
                Catalog <span className="font-mono">{catalog.catalog}</span> is{' '}
                {proposal.verification_result?.assigned ? '' : 'not '}assigned to role{' '}
                <span className="font-mono">{proposal.role}</span>.
              </p>
              <p className="text-muted-foreground">
                Source: {proposal.verification_result?.source || 'SAP role menu read'}. Read-only result; no approval, transport, or SAP write is required.
              </p>
            </div>
          ) : catalogAdd ? (
            <div data-testid="menu-change-additions">
              <p className="font-semibold" data-testid="menu-change-one-assignment">
                One catalog assignment
              </p>
              <p>
                Add catalog {catalog.catalog} to role {proposal.role}. This approval is one catalog
                assignment{contents.length > 0 ? `, not ${contents.length} separate changes` : ''}.
              </p>
            </div>
          ) : applicationToCatalog ? (
            <div data-testid="menu-change-content-proposal">
              <p className="font-semibold">One tile and one target mapping</p>
              <p>
                Add {catalog.fioriId || catalog.application || 'the verified application'} to catalog{' '}
                <span className="font-mono">{catalog.destinationCatalog || catalog.catalog}</span>
                {catalog.sourceCatalog ? <> from <span className="font-mono">{catalog.sourceCatalog}</span></> : null}.
              </p>
              <p>The tile and target mapping come from the same source row. This is not a catalog-to-role assignment.</p>
              {(catalog.tileId || catalog.targetMappingId) && (
                <details data-testid="menu-change-copy-ids">
                  <summary className="cursor-pointer">Technical configuration</summary>
                  {catalog.tileId ? <p className="font-mono text-xs">Catalog-local tile: {catalog.tileId}</p> : null}
                  {catalog.targetMappingId ? <p className="font-mono text-xs">Catalog-local target mapping: {catalog.targetMappingId}</p> : null}
                </details>
              )}
              {catalog.navigationVerified ? null : (
                <p data-testid="menu-change-navigation-limitation">
                  Navigation parameters were not returned and remain unverified. The catalog writer stays blocked.
                </p>
              )}
            </div>
          ) : (
            <div data-testid="menu-change-additions">{additions.length} proposed addition(s)</div>
          )}
          {!catalogAdd && !catalogVerification && additions.length > 0 && (
            <ul className="list-disc pl-5">
              {additions.map((row) => (
                <li key={`${row.object_type}-${row.obj_name}`} className="font-mono">{contentLabel(row)}</li>
              ))}
            </ul>
          )}
          {showRefreshDelta && (
            <>
              <div data-testid="menu-change-removals">{removals.length} potential removal(s), none preselected</div>
              <div data-testid="menu-change-unresolved">{unresolved.length} unresolved difference(s)</div>
            </>
          )}
          {catalogVerification && (
            <div data-testid="menu-change-current-assignments">
              <p className="font-medium">Current SAP assignments</p>
              <CatalogList rows={asRows(proposal.verification_result?.assigned_catalogs)} added="" />
            </div>
          )}
          {!catalogVerification && (catalog.before || catalog.after) && (
            <div className="grid gap-3 md:grid-cols-2">
              <BeforeAfter
                side="Before"
                value={catalog.before}
                testId="menu-change-before"
              />
              <BeforeAfter
                side="After"
                value={catalog.after}
                added={catalog.catalog}
                addedService={service ? identity : null}
                testId="menu-change-after"
              />
            </div>
          )}
        </Section>

        {!catalogAdd && catalog.mappings.length > 0 && (
          <div data-testid="menu-change-mappings">
            {catalog.mappings.map((row) => (
              <div key={`${row.catalog_id || ''}-${row.instance_id || row.title || row.application || ''}`}>
                Mapping {row.title || row.application || row.obj_name || ''}
                {row.semantic_object ? ` ${row.semantic_object}/${row.semantic_action || ''}` : ''}
                {row.fiori_id ? ` ${row.fiori_id}` : ''}
                {row.provenance?.scope ? ` ${row.provenance.scope}` : ''}
                {row.provenance?.live_sap === false || row.simulated === true ? ' SIMULATED' : ''}
              </div>
            ))}
          </div>
        )}

        {contents.length > 0 && (
          <Section
            title={catalogAdd || catalogVerification ? 'Catalog content' : 'Other catalog content'}
            testId="menu-change-additional-apps"
          >
            <p>
              {catalogVerification
                ? `Catalog ${catalog.catalog} contains ${contents.length} content entr${contents.length === 1 ? 'y' : 'ies'}`
                : catalogAdd
                ? `Catalog ${catalog.catalog} contains ${contents.length} content entr${contents.length === 1 ? 'y' : 'ies'}`
                : `SAP returned ${contents.length} other content entr${contents.length === 1 ? 'y' : 'ies'}`}
              {contentTypes.length > 0
                ? ` (${contentTypes.map(([kind, count]) => `${count} ${kind}`).join(', ')})`
                : ''}
              . {catalogVerification
                ? 'These entries were read from SAP. They are not proposed changes.'
                : 'These rows describe the catalog. They are not separate approvals, and they are not all separate Fiori applications. This assignment does not grant backend authorization.'}
            </p>
            {contentTypes.length > 0 && (
              <ul className="list-disc pl-5" data-testid="menu-change-content-breakdown">
                {contentTypes.map(([kind, count]) => (
                  <li key={kind}>{count} {kind}</li>
                ))}
              </ul>
            )}
            <details>
              <summary className="cursor-pointer">Show all {contents.length} entries</summary>
              <ul className="mt-1 max-h-64 overflow-y-auto pl-5 font-mono text-xs list-disc">
                {contents.map((row, index) => (
                  <li key={`${contentLabel(row)}-${index}`}>{contentLabel(row)}</li>
                ))}
              </ul>
            </details>
          </Section>
        )}

        {catalog.transportPlan?.requests?.length > 0 && (
          <Section title="Transport plan" testId="menu-change-transport-plan">
            <p>{catalog.transportPlan.reason}</p>
            <p>Operation order: {(catalog.transportPlan.movement_order || []).join(' then ')}. The shared request is not created or released by approval.</p>
            <ul className="list-disc pl-5">
              {catalog.transportPlan.requests.map((item) => (
                <li key={item.sequence}>
                  Customizing transport: {item.purpose}. {item.transport_type}. {item.status}.
                  {item.role ? ` Role ${item.role}.` : ''}
                </li>
              ))}
            </ul>
            {catalog.transportPlan.shared_catalog_follow_up ? <p>{catalog.transportPlan.shared_catalog_follow_up}</p> : null}
          </Section>
        )}

        {catalog.roleMaintenancePlan && (
          <Section title="Target-role maintenance" testId="menu-change-role-maintenance">
            <p>{catalog.roleMaintenancePlan.detail}</p>
            <p>
              {catalog.roleMaintenancePlan.transport_action === 'catalog_objects_only'
                ? <>The catalog save does not change role <span className="font-mono">{catalog.roleMaintenancePlan.target_role}</span>, so <span className="font-mono">R3TR ACGR</span> is not added for that save. The later single-role refresh records it on the same request only if the role changes.</>
                : <>Role <span className="font-mono">{catalog.roleMaintenancePlan.target_role}</span> is recorded as <span className="font-mono">R3TR ACGR</span> on the shared request.</>}
            </p>
            <p>
              Profile generation: {catalog.roleMaintenancePlan.profile_generation}.
              {' '}Other referencing roles are not modified.
            </p>
          </Section>
        )}

        {catalogAdd && (
          <Section title="Target-role impact" testId="menu-change-target-role-impact">
            <p>
              This operation assigns the existing catalog to role <span className="font-mono">{proposal.role}</span>.
              It does not modify the catalog contents or any other role.
            </p>
          </Section>
        )}

        {catalogContentModification && catalog.hasImpact && (
          <Section title="Shared-role impact" testId="menu-change-impact">
            {!catalog.impactComplete ? (
              <div
                data-testid="menu-change-impact-unknown"
                className="rounded-md border border-cp-mod px-3 py-2 text-cp-mod"
              >
                Shared-catalog impact is incomplete and requires human review.
                {catalog.rolesScanned != null ? ` HALO checked ${catalog.rolesScanned} role(s)` : ' HALO did not check every role'}
                {catalog.scanTruncated ? ' and the scan stopped at the configured role limit' : ''}.
                {catalog.failedRoleReads.length > 0 ? ` ${catalog.failedRoleReads.length} role read(s) failed.` : ''}
                {' '}Finding {catalog.affectedRoles.length} matching role(s) in a partial scan does not mean no
                other role references this catalog.
              </div>
            ) : (
              <p data-testid="menu-change-role-count">
                {catalog.roleCount !== null
                  ? `${catalog.roleCount} distinct PFCG role(s) reference this catalog.`
                  : `${catalog.affectedRoles.length} role(s) reference this catalog.`}
                {' '}Only {proposal.role} is in scope for later role maintenance.
              </p>
            )}
            {catalog.affectedRoles.length > 0 && (
              <div data-testid="menu-change-affected-roles">
                Roles referencing this catalog: {catalog.affectedRoles.join(', ')}
              </div>
            )}
          </Section>
        )}

        <Section title="Analysis">
          {(disposition === 'proposal_only' || disposition === 'unsupported') && (
            <p data-testid="menu-change-disposition">
              {disposition === 'unsupported' ? 'Unsupported' : 'Proposal only'}
              {catalog.blocker ? ` (${catalog.blocker})` : ''}. No SAP write is available for this operation.
            </p>
          )}
          {disposition === 'read_only' && (
            <p data-testid="menu-change-read-only">
              Read-only verification. HALO did not propose or execute a catalog modification.
            </p>
          )}
          {decision === 'rejected' && (
            <p data-testid="menu-change-rejection">
              Rejected{change.rejection?.by ? ` by ${change.rejection.by}` : ''}. No SAP write was performed. This is not a technical failure.
            </p>
          )}
          {decision === 'failed' && (
            <p data-testid="menu-change-technical-failure">
              Technical failure. The menu change did not complete.
            </p>
          )}
          {proposal.operation_explanation && (
            <p data-testid="menu-change-operation-meaning">{proposal.operation_explanation}</p>
          )}
          <p>{proposal.authorization_summary || 'Authorization changes require a separate approval.'}</p>
          {proposal.executable === false && !['APPROVED', 'APPLIED'].includes(change.lifecycle) && disposition !== 'read_only' && (
            <p data-testid="menu-change-not-executed">Not executed. This proposal did not change SAP.</p>
          )}
          {['VERIFY_ALREADY_ASSIGNED', 'VERIFY_ALREADY_AVAILABLE', 'VERIFY_ALREADY_PRESENT'].includes(operation) && (
            <p data-testid="menu-change-already-satisfied">Already satisfied. No duplicate SAP write.</p>
          )}
          {catalogAdd && pending && (
            <p data-testid="menu-change-executable">
              Executable catalog-to-role assignment. SAP stays unchanged until approval, and approval
              does not grant backend authorization.
            </p>
          )}
          {applicationToCatalog && proposal.writer_readiness?.ready === true && pending && (
            <p data-testid="menu-change-executable">
              Approval applies this exact catalog change, verifies it, and continues to the PFCG refresh.
              Authorization cleanup stays a separate approval.
            </p>
          )}
          {catalog.blocker && disposition !== 'read_only' && (
            <p data-testid="menu-change-blocker">Execution blocker {catalog.blocker}</p>
          )}
        </Section>

        {Array.isArray(proposal.alternatives) && proposal.alternatives.length > 0 && (
          <div data-testid="menu-change-alternatives">
            {proposal.alternatives.map((item) => (
              <div key={item.catalog_id}>
                Not selected: {item.catalog_id}. {item.note}
                {Array.isArray(item.additional_applications) && item.additional_applications.length > 0
                  ? ` Other catalog content: ${item.additional_applications.map((row) => row.obj_name || row.application || row.title).filter(Boolean).join(', ')}.`
                  : ''}
              </div>
            ))}
          </div>
        )}

        {execution.status && (
          <div data-testid="menu-change-execution">{execution.status}</div>
        )}
        {operation === 'APPLICATION_TO_CATALOG' && execution.status && (
          <div data-testid="menu-change-catalog-progress">
            Catalog verification: {execution.catalog_verified ? 'verified' : 'not verified'}.
            {' '}Transport contents: {execution.transport_verified ? 'verified' : 'not verified'}.
          </div>
        )}
        {execution.status && (
          <div data-testid="menu-change-transport">Transport {execution.transport || 'not returned'}</div>
        )}
        {execution.sap_changed === true && (
          <div data-testid="menu-change-sap">SAP verification: changed</div>
        )}
        {execution.sap_changed === false && execution.status && (
          <div data-testid="menu-change-sap">SAP verification: unchanged</div>
        )}
        {change.lifecycle === 'RECONCILIATION_REQUIRED' && (
          <div data-testid="menu-change-reconciliation">Reconciliation required. Do not retry the SAP write.</div>
        )}
        {change.lifecycle === 'PARTIAL_SUCCESS' && (
          <div data-testid="menu-change-partial">SAP committed the menu change. Transport recording still needs reconciliation.</div>
        )}
        {pending && canMutate && (
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="menu-change-approve"
              disabled={submitting}
              onClick={() => onApprove?.({
                payloadHash: proposal.payload_hash,
                menuStateHash: proposal.menu_state_hash,
                reviewHash: catalog.reviewHash,
              })}
            >
              {applicationToCatalog ? 'Approve & Apply Catalog Change' : 'Approve menu change'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="menu-change-reject"
              disabled={submitting}
              onClick={() => onReject?.({
                payloadHash: proposal.payload_hash,
                menuStateHash: proposal.menu_state_hash,
                reviewHash: catalog.reviewHash,
              })}
            >
              Reject menu change
            </Button>
          </div>
        )}
        {approvedCatalogContent && canMutate && execution.catalog_verified !== true && execution.sap_changed !== true && execution.status && (
          <p data-testid="menu-change-catalog-retry">
            Catalog apply did not verify. {execution.detail || execution.status}. The catalog writer is not repeated until this failure is recovered.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
